/**
 * SSR metadata for database-backed dynamic routes (blog posts, white papers,
 * case studies, news, regulatory updates, podcasts, webinars, events, authors).
 *
 * Dynamic routes have no entry in src/generated/seoRouteMeta.ts (their paths are
 * per-slug), so without this they'd all inherit the generic site-wide title.
 * The route loader fetches the row's title/summary straight from the REST API
 * (works during SSR and on the client, no Supabase JS client needed) and the
 * route `head()` turns it into a unique title/description/og:image.
 */
import { buildRouteHead, type RouteHeadOverrides } from "./routeHead";
import { SITE_NAME, SITE_URL } from "@/config/site";

export type ContentTable =
  | "blog_posts"
  | "white_papers"
  | "checklists"
  | "case_studies"
  | "company_news"
  | "regulatory_updates"
  | "podcasts"
  | "webinars"
  | "events"
  | "authors";

export interface ContentMeta {
  title?: string;
  /** Raw row title (no site-name suffix), used as the page's SSR <h1>. */
  heading?: string;
  description?: string;
  ogImage?: string;
  /** Row is tagged "_hidden": reachable by direct URL only, never indexed. */
  hidden?: boolean;
  /** Published article body (markdown/HTML), rendered into the SSR HTML. */
  body?: string;
  author?: string;
  publishedAt?: string;
  updatedAt?: string;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

/** Which localized slug columns each table actually has. */
const SLUG_COLUMNS: Record<ContentTable, string[]> = {
  blog_posts: ["slug", "slug_es"],
  white_papers: ["slug", "slug_es", "slug_zh"],
  checklists: ["slug", "slug_es", "slug_zh"],
  case_studies: ["slug", "slug_es", "slug_zh"],
  company_news: ["slug", "slug_es", "slug_zh"],
  regulatory_updates: ["slug", "slug_es"],
  podcasts: ["slug", "slug_es"],
  webinars: ["slug", "slug_es", "slug_zh"],
  events: ["slug", "slug_es"],
  authors: ["slug"],
};

/** Columns needed to build a title + description for each table. */
const SELECT_COLUMNS: Record<ContentTable, string> = {
  blog_posts: "title,summary,featured_image,slug,slug_es",
  // Rows that localize in place also need their localized bodies so the
  // server-rendered article text matches the page language.
  white_papers:
    "title,title_es,title_zh,summary,summary_es,summary_zh,featured_image,slug,slug_es,slug_zh,content_es,content_zh",
  checklists:
    "title,title_es,title_zh,summary,summary_es,summary_zh,featured_image,slug,slug_es,slug_zh,content_es,content_zh",
  case_studies:
    "title,title_es,title_zh,summary,summary_es,summary_zh,featured_image,slug,slug_es,slug_zh,content_es,content_zh",
  company_news:
    "title,title_es,title_zh,summary,summary_es,summary_zh,featured_image,slug,slug_es,slug_zh,content_es,content_zh",
  regulatory_updates: "title,title_es,summary,summary_es,featured_image,slug,slug_es,content_es",
  podcasts: "title,title_es,summary,summary_es,featured_image,slug,slug_es,content_es",
  webinars:
    "title,title_es,title_zh,summary,summary_es,summary_zh,featured_image,slug,slug_es,slug_zh,parent_slug,tags,content_es,content_zh",
  events: "title,title_es,summary,summary_es,featured_image,slug,slug_es,content_es",
  authors: "name,role,bio,bio_es,bio_zh,avatar_url,slug",
};

/** Tables that keep one row per language in a `lang` column. */
/** Extra columns for the server-rendered article body and Article schema. */
const BODY_COLUMNS = "content,author,published_at,updated_at";

const LANG_SCOPED_TABLES = new Set<ContentTable>(["blog_posts", "webinars"]);

/** Suffix appended to content titles, per language. */
const TITLE_SUFFIX: Record<string, string> = {
  en: ` | ${SITE_NAME}`,
  es: ` | ${SITE_NAME}`,
  "zh-cn": ` | ${SITE_NAME}`,
};

const stripMarkup = (value: string): string =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#*_`>]/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const clamp = (value: string, max = 158): string => {
  const clean = stripMarkup(value);
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
};

/** Pick the localized variant of a column, falling back to the base column. */
const localized = (row: Record<string, any>, base: string, lang: string): string | undefined => {
  const suffix = lang === "es" ? "_es" : lang === "zh-cn" ? "_zh" : "";
  const value = (suffix && row[`${base}${suffix}`]) || row[base];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

/**
 * Loader helper: resolve unique metadata for one content row.
 * Never throws — a network or schema hiccup just falls back to route defaults.
 */
export async function loadContentMeta(
  table: ContentTable,
  slug: string | undefined,
  lang: string,
): Promise<ContentMeta | null> {
  if (!slug || !SUPABASE_URL || !SUPABASE_KEY) return null;

  const selectCols =
    table === "authors" ? SELECT_COLUMNS[table] : `${SELECT_COLUMNS[table]},${BODY_COLUMNS}`;
  const orFilter = SLUG_COLUMNS[table].map((c) => `${c}.eq.${encodeURIComponent(slug)}`).join(",");
  const baseUrl =
    `${SUPABASE_URL}/rest/v1/${table}` +
    `?select=${encodeURIComponent(selectCols)}` +
    `&or=(${orFilter})&limit=1`;

  // Tables that store one row per language: a Spanish slug can also live in the
  // English row's slug_es column, so filter on the row's own language first to
  // avoid returning the English title for a Spanish URL.
  const langFilteredUrl = LANG_SCOPED_TABLES.has(table)
    ? `${baseUrl}&lang=eq.${encodeURIComponent(lang)}`
    : undefined;

  const fetchRow = async (target: string): Promise<Record<string, any> | null> => {
    const res = await fetch(target, {
      headers: { apikey: SUPABASE_KEY, Accept: "application/json" },
    });
    if (!res.ok) return null;
    const rows = (await res.json()) as Record<string, any>[];
    return Array.isArray(rows) && rows[0] ? rows[0] : null;
  };

  try {
    // Chinese rows keep a "-zh" slug suffix while the public URL drops it, so the
    // unsuffixed slug must also be tried against the suffixed column value —
    // otherwise the English row wins and the page gets an English title.
    const zhSuffixedUrl =
      lang === "zh-cn" && LANG_SCOPED_TABLES.has(table)
        ? `${SUPABASE_URL}/rest/v1/${table}` +
          `?select=${encodeURIComponent(selectCols)}` +
          `&slug=eq.${encodeURIComponent(`${slug}-zh`)}&lang=eq.zh-cn&limit=1`
        : undefined;

    const row =
      (langFilteredUrl ? await fetchRow(langFilteredUrl) : null) ??
      (zhSuffixedUrl ? await fetchRow(zhSuffixedUrl) : null) ??
      (await fetchRow(baseUrl));
    if (!row) return null;


    const suffix = TITLE_SUFFIX[lang] ?? TITLE_SUFFIX.en;

    if (table === "authors") {
      const name = typeof row.name === "string" ? row.name.trim() : "";
      if (!name) return null;
      const bio = localized(row, "bio", lang);
      const role = typeof row.role === "string" ? row.role.trim() : "";
      return {
        title: `${name}${role ? ` — ${role}` : ""}${suffix}`,
        heading: name,
        description: bio
          ? clamp(bio)
          : `Articles, insights and expertise from ${name} at ${SITE_NAME}.`,
        ogImage: typeof row.avatar_url === "string" ? row.avatar_url : undefined,
      };
    }

    const title = localized(row, "title", lang);
    if (!title) return null;

    // Live webinars and their on-demand recordings share one title. They are two
    // separate indexable pages, so label the recording to keep titles unique.
    // On-demand recordings carry a parent_slug pointing at the live session; some
    // older rows only encode it in the slug.
    const isOnDemand =
      table === "webinars" &&
      (typeof row.parent_slug === "string" && row.parent_slug.trim().length > 0
        ? true
        : /on-demand|bajo-demanda/.test(slug));
    const onDemandLabel =
      lang === "es" ? "Webinar On-Demand" : lang === "zh-cn" ? "点播网络研讨会" : "On-Demand Webinar";
    const summary = localized(row, "summary", lang);
    // Only published rows expose their body in SSR HTML — drafts stay private.
    const published = typeof row.published_at === "string" && row.published_at;
    const rawBody = published ? localized(row, "content", lang) : undefined;
    const body = rawBody?.replace(/<!--[\s\S]*?-->/g, "").trim() || undefined;

    return {
      title: `${stripMarkup(title)}${isOnDemand ? ` — ${onDemandLabel}` : ""}${suffix}`,
      heading: stripMarkup(title),
      description: summary ? clamp(summary) : undefined,
      ogImage: typeof row.featured_image === "string" ? row.featured_image : undefined,
      body,
      author: typeof row.author === "string" && row.author.trim() ? row.author.trim() : undefined,
      publishedAt: published || undefined,
      updatedAt: typeof row.updated_at === "string" ? row.updated_at : undefined,
      hidden: Array.isArray(row.tags) && row.tags.some((t: unknown) => String(t).toLowerCase() === "_hidden"),
    };
  } catch {
    return null;
  }
}

/** Turn loaded content metadata into buildRouteHead overrides. */
export function contentHeadOverrides(
  meta: ContentMeta | null | undefined,
  extra: RouteHeadOverrides = {},
): RouteHeadOverrides {
  return {
    ...(meta?.title ? { title: meta.title } : {}),
    ...(meta?.description ? { description: meta.description } : {}),
    ...(meta?.ogImage ? { ogImage: meta.ogImage } : {}),
    ogType: "article",
    ...(meta?.hidden ? { noIndex: true } : {}),
    ...extra,
  };
}

/** Convenience wrapper used directly by dynamic route `head()` options. */
export function buildContentHead(
  path: string,
  meta: ContentMeta | null | undefined,
  extra: RouteHeadOverrides = {},
) {
  const head = buildRouteHead(path, contentHeadOverrides(meta, extra));
  if (!meta?.heading || !meta.publishedAt) return head;
  const url = `${SITE_URL}${path}`;
  const segments = path.split("/").filter(Boolean);
  const sectionPath = `/${segments.slice(0, -1).join("/")}`;
  const sectionName = (segments[segments.length - 2] ?? "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const publisher = {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/assets/qbd-logo.webp` },
  };
  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: meta.heading,
    ...(meta.description ? { description: meta.description } : {}),
    ...(meta.ogImage ? { image: meta.ogImage } : {}),
    datePublished: meta.publishedAt,
    ...(meta.updatedAt ? { dateModified: meta.updatedAt } : {}),
    author: meta.author
      ? meta.author.split(/\s*&\s*/).map((name) => ({ "@type": "Person", name }))
      : { "@type": "Organization", name: SITE_NAME },
    publisher,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/${segments[0] ?? "en"}` },
      { "@type": "ListItem", position: 2, name: sectionName, item: `${SITE_URL}${sectionPath}` },
      { "@type": "ListItem", position: 3, name: meta.heading, item: url },
    ],
  };
  return {
    ...head,
    scripts: [article, breadcrumb].map((data) => ({
      type: "application/ld+json",
      children: JSON.stringify(data),
    })),
  };
}
